import { Badge } from '@/components/ui/badge';
import type { ItemStatus, DeviceStatus, LocationStatus, SerializedItemStatus } from '../../types';

// 5S Status colors matching the workshop poster
const itemStatusConfig: Record<ItemStatus, { label: string; className: string }> = {
  valid: { label: 'Valid', className: 'bg-green-500 hover:bg-green-600 text-white' },
  verify: { label: 'Verify', className: 'bg-yellow-500 hover:bg-yellow-600 text-black' },
  test: { label: 'Test', className: 'bg-blue-500 hover:bg-blue-600 text-white' },
  dev: { label: 'Dev', className: 'bg-purple-500 hover:bg-purple-600 text-white' },
  dead: { label: 'Dead', className: 'bg-red-500 hover:bg-red-600 text-white' },
};

const deviceStatusConfig: Record<DeviceStatus, { label: string; className: string }> = {
  valid: { label: 'Valid', className: 'bg-green-500 hover:bg-green-600 text-white' },
  verify: { label: 'Verify', className: 'bg-yellow-500 hover:bg-yellow-600 text-black' },
  test: { label: 'Test', className: 'bg-blue-500 hover:bg-blue-600 text-white' },
  dev: { label: 'Dev', className: 'bg-purple-500 hover:bg-purple-600 text-white' },
  dead: { label: 'Dead', className: 'bg-red-500 hover:bg-red-600 text-white' },
};

const locationStatusConfig: Record<LocationStatus, { label: string; className: string }> = {
  active: { label: 'Active', className: 'bg-green-500 hover:bg-green-600 text-white' },
  full: { label: 'Full', className: 'bg-orange-500 hover:bg-orange-600 text-white' },
  reserved: { label: 'Reserved', className: 'bg-blue-500 hover:bg-blue-600 text-white' },
  maintenance: { label: 'Maintenance', className: 'bg-yellow-500 hover:bg-yellow-600 text-black' },
  inactive: { label: 'Inactive', className: 'bg-gray-500 hover:bg-gray-600 text-white' },
};

const serializedStatusConfig: Record<SerializedItemStatus, { label: string; className: string }> = {
  in_stock: { label: 'In Stock', className: 'bg-green-500 hover:bg-green-600 text-white' },
  reserved: { label: 'Reserved', className: 'bg-blue-500 hover:bg-blue-600 text-white' },
  allocated: { label: 'Allocated', className: 'bg-purple-500 hover:bg-purple-600 text-white' },
  shipped: { label: 'Shipped', className: 'bg-cyan-500 hover:bg-cyan-600 text-white' },
  installed: { label: 'Installed', className: 'bg-teal-500 hover:bg-teal-600 text-white' },
  rma: { label: 'RMA', className: 'bg-orange-500 hover:bg-orange-600 text-white' },
  scrapped: { label: 'Scrapped', className: 'bg-red-500 hover:bg-red-600 text-white' },
};

interface ItemStatusBadgeProps {
  status: ItemStatus;
}

export function ItemStatusBadge({ status }: ItemStatusBadgeProps) {
  const config = itemStatusConfig[status] || { label: status, className: 'bg-gray-500' };
  return <Badge className={config.className}>{config.label}</Badge>;
}

interface DeviceStatusBadgeProps {
  status: DeviceStatus;
}

export function DeviceStatusBadge({ status }: DeviceStatusBadgeProps) {
  const config = deviceStatusConfig[status] || { label: status, className: 'bg-gray-500' };
  return <Badge className={config.className}>{config.label}</Badge>;
}

interface LocationStatusBadgeProps {
  status: LocationStatus;
}

export function LocationStatusBadge({ status }: LocationStatusBadgeProps) {
  const config = locationStatusConfig[status] || { label: status, className: 'bg-gray-500' };
  return <Badge className={config.className}>{config.label}</Badge>;
}

interface SerializedStatusBadgeProps {
  status: SerializedItemStatus;
}

export function SerializedStatusBadge({ status }: SerializedStatusBadgeProps) {
  const config = serializedStatusConfig[status] || { label: status, className: 'bg-gray-500' };
  return <Badge className={config.className}>{config.label}</Badge>;
}

// Alias for consistency
export const SerializedItemStatusBadge = SerializedStatusBadge;
